import { useCallback, useEffect, useRef, useState } from "react";
import SessionDetails from "./SessionDetails";
import styles from "../../_css/Home.module.css";
import ReviewCard from "./ReviewCard";
import ReviewHistory from "./ReviewHistory";
import {
  getSessionReview,
  getSessionSummary,
  handleReview,
  resumeSession,
} from "../../requests";

import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper";
import { DataLoader } from "../common/ResponseLoader";
import {
  HOME_DATA_READ_MORE,
  HOME_DATA_SESSION_DETAILS,
} from "../../data/staticDataHome";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { pdf } from "@react-pdf/renderer";
import AddReviewModal from "../Modals/AddReviewModal";
import DownloadSummary from "../Modals/DownloadSummary";
import StartChat from "../Modals/StartChat";
import ChatPopup from "../Modals/ChatPopup";
import VoiceChatPopup from "../Modals/VoiceChatPopup";
import PDFGenerator from "../helper/PDFGenerator";
import { formatDate, isEnglishHtml } from "../helper/formatters";
import { unlockAudioPlayback } from "../../utils/audioPlaybackManager";
import { prewarmMicForGesture } from "../../utils/micStreamManager";

const ALL_REVIEWS_PAGE_SIZE = 6;
const USER_REVIEWS_PAGE_SIZE = 6;

const defaultPagination = {
  currentPage: 1,
  hasNextPage: false,
  nextPage: null,
};

function ReadMore() {
  const [loading, setLoading] = useState(false);
  const [loadingMoreAllReviews, setLoadingMoreAllReviews] = useState(false);
  const [loadingMoreUserReviews, setLoadingMoreUserReviews] = useState(false);
  const [reviewData, setReviewData] = useState({
    allReviews: [],
    userReviews: [],
  });
  const [allReviewsPagination, setAllReviewsPagination] =
    useState(defaultPagination);
  const [userReviewsPagination, setUserReviewsPagination] =
    useState(defaultPagination);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [showDownloadSummary, setShowDownloadSummary] = useState(false);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [showStartChat, setShowStartChat] = useState(false);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);

  const reviewsContainerRef = useRef(null);
  const allReviewsLoadMoreRef = useRef(null);
  const reviewHistoryContainerRef = useRef(null);
  const userReviewsLoadMoreRef = useRef(null);
  const isFetchingRef = useRef(false);

  const dispatch = useDispatch();

  const sessionData = sessionStorage.getItem("sessionData");
  const sessionDataParsed = JSON.parse(sessionData);

  const lang = useSelector((state) => state.language.lang);
  const { reviewsTitle, noReviews } = HOME_DATA_READ_MORE[lang];
  const {
    pdfHeaderText,
    pdfFooterText,
    generatingPdfText,
    sendingRequestText,
    downloadedText,
    btnText,
    reviewAddedSuccessfully,
    reviewUpdatedSuccessfully,
  } = HOME_DATA_SESSION_DETAILS[lang];

  const fetchReviews = useCallback(
    async ({
      allReviewsPage = 1,
      userReviewsPage = 1,
      appendAll = false,
      appendUser = false,
      updateAll = true,
      updateUser = true,
    } = {}) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      const isInitialLoad =
        updateAll &&
        updateUser &&
        allReviewsPage === 1 &&
        userReviewsPage === 1 &&
        !appendAll &&
        !appendUser;

      if (isInitialLoad) {
        setLoading(true);
      } else if (appendAll) {
        setLoadingMoreAllReviews(true);
      } else if (appendUser) {
        setLoadingMoreUserReviews(true);
      }

      //delay for 1 second
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      try {
        const response = await getSessionReview({
          trainingSessionId: sessionDataParsed?.id,
          isPagination: true,
          allReviewsPage,
          allReviewsLimit: ALL_REVIEWS_PAGE_SIZE,
          userReviewsPage,
          userReviewsLimit: USER_REVIEWS_PAGE_SIZE,
        });

        const {
          all_reviews: allReviews,
          user_reviews: userReviews,
          allReviewsPagination: allPagination,
          userReviewsPagination: userPagination,
        } = response.data;

        if (updateAll) {
          setReviewData((prev) => ({
            ...prev,
            allReviews: appendAll
              ? [...prev.allReviews, ...allReviews]
              : allReviews,
          }));
          setAllReviewsPagination(allPagination || defaultPagination);
        }

        if (updateUser) {
          setReviewData((prev) => ({
            ...prev,
            userReviews: appendUser
              ? [...prev.userReviews, ...userReviews]
              : userReviews,
          }));
          setUserReviewsPagination(userPagination || defaultPagination);
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          deleteAuthToken();
          window.location.reload();
        }
        console.error("Error Sending Message:", error);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
        setLoadingMoreAllReviews(false);
        setLoadingMoreUserReviews(false);
      }
    },
    [sessionDataParsed?.id]
  );

  const refreshReviews = useCallback(() => {
    fetchReviews({
      allReviewsPage: 1,
      userReviewsPage: 1,
    });
  }, [fetchReviews]);

  const prevChatPopupRef = useRef(showChatPopup);
  const prevVoicePopupRef = useRef(showVoicePopup);

  useEffect(() => {
    refreshReviews();
  }, [refreshReviews]);

  useEffect(() => {
    const wasChatOpen = prevChatPopupRef.current;
    const wasVoiceOpen = prevVoicePopupRef.current;
    prevChatPopupRef.current = showChatPopup;
    prevVoicePopupRef.current = showVoicePopup;

    if (
      (wasChatOpen && !showChatPopup) ||
      (wasVoiceOpen && !showVoicePopup)
    ) {
      refreshReviews();
    }
  }, [showChatPopup, showVoicePopup, refreshReviews]);

  useEffect(() => {
    const container = reviewsContainerRef.current;
    const sentinel = allReviewsLoadMoreRef.current;
    if (!container || !sentinel || !reviewsExpanded) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          allReviewsPagination.hasNextPage &&
          !isFetchingRef.current
        ) {
          const nextPage =
            allReviewsPagination.nextPage ??
            allReviewsPagination.currentPage + 1;
          fetchReviews({
            allReviewsPage: nextPage,
            userReviewsPage: 1,
            appendAll: true,
            appendUser: false,
            updateAll: true,
            updateUser: false,
          });
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    allReviewsPagination.hasNextPage,
    allReviewsPagination.nextPage,
    allReviewsPagination.currentPage,
    fetchReviews,
    reviewData.allReviews.length,
    reviewsExpanded,
  ]);

  useEffect(() => {
    const container = reviewHistoryContainerRef.current;
    const sentinel = userReviewsLoadMoreRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          userReviewsPagination.hasNextPage &&
          !isFetchingRef.current
        ) {
          const nextPage =
            userReviewsPagination.nextPage ??
            userReviewsPagination.currentPage + 1;
          fetchReviews({
            allReviewsPage: 1,
            userReviewsPage: nextPage,
            appendAll: false,
            appendUser: true,
            updateAll: false,
            updateUser: true,
          });
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    userReviewsPagination.hasNextPage,
    userReviewsPagination.nextPage,
    userReviewsPagination.currentPage,
    fetchReviews,
    reviewData.userReviews.length,
  ]);

  const onTextHandle = () => {
    setShowStartChat(false);
    setShowChatPopup(true);
  };
  const onVoiceHandle = () => {
    unlockAudioPlayback();
    prewarmMicForGesture();
    setShowStartChat(false);
    setShowVoicePopup(true);
  };

  const handleDownloadSummary = async () => {
    const userSessionId = sessionStorage.getItem("userSessionId");
    setPdfStatus(sendingRequestText);
    try {
      const response = await getSessionSummary({
        userSessionId,
      });
      const summary = response.data.message;
      const created_at = response.data.created_at;
      setPdfStatus(generatingPdfText);
      const isEnglish = isEnglishHtml(summary);
      const formattedDate =
        formatDate(created_at) || new Date().toISOString().split("T")[0];

      // Generate PDF
      const pdfDoc = (
        <PDFGenerator
          summary={summary}
          title={
            !isEnglish
              ? sessionDataParsed?.title_arabic
              : sessionDataParsed?.title || pdfHeaderText
          }
          pdfHeaderText={pdfHeaderText}
          pdfFooterText={"Generated on - " + formattedDate}
        />
      );

      // Create PDF blob
      const pdfBlob = await pdf(pdfDoc).toBlob();

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `session-summary-${
        new Date().toISOString().split("T")[0]
      }.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      setPdfStatus(downloadedText);
    } catch (error) {
      console.log(error);
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError("Something went wrong");
    }

    refreshReviews();

    // check whether the review is added or not for a particular session
    const review = reviewData.userReviews.find(
      (review) => review.user_session_id == userSessionId
    );

    if (review && !review.review_id) {
      setShowAddReviewModal(true);
    }

    setShowDownloadSummary(false);
    setShowChatPopup(false);
    setShowVoicePopup(false);
  };

  const handleAddReview = async (review, ratings) => {
    console.log("ratings", ratings);
    if (!ratings) {
      notifyError("Please fill all the fields");
      return;
    }
    setShowAddReviewModal(false);
    dispatch(startLoader());
    const userSessionId = sessionStorage.getItem("userSessionId");
    try {
      const response = await handleReview({
        review: review,
        ratings: ratings,
        user_session_id: userSessionId,
      });
      if (response.status === 200) {
        if (response?.data?.message == "Review updated successfully") {
          notifySuccess(reviewUpdatedSuccessfully);
        } else {
          notifySuccess(reviewAddedSuccessfully);
        }
        refreshReviews();
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(
        lang === "ar" ? "فشل إضافة التقييم" : error?.response?.data?.message
      );
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  const handleResumeSession = async (isResume = false) => {
    unlockAudioPlayback();
    prewarmMicForGesture();
    dispatch(startLoader());
    console.log("loader started..");

    sessionStorage.removeItem("resumeSessionData");
    sessionStorage.removeItem("chatHistory");

    setPdfStatus(null);

    const sessionData = sessionStorage.getItem("sessionData");
    const sessionDataParsed = JSON.parse(sessionData);
    const sessionId = sessionDataParsed?.id;

    const userSessionId = sessionStorage.getItem("userSessionId");

    try {
      console.log("Requesting info for userSessionId: ", userSessionId);
      const response = await resumeSession({
        sessionId: sessionId,
        userSessionId: userSessionId,
        isResume,
      });
      if (response.status === 200) {
        const { mode } = response.data.sessionInfo;
        console.log("sessionInfo: ", response.data.sessionInfo);

        // store session info
        sessionStorage.setItem(
          "resumeSessionData",
          JSON.stringify(response.data.sessionInfo)
        );
        const chatHistory = response.data.chatHistory;
        // store chat history
        sessionStorage.setItem("chatHistory", JSON.stringify(chatHistory));

        if (mode === "text") {
          setShowChatPopup(true);
        } else if (mode === "voice") {
          setShowVoicePopup(true);
        }
      } else if (response.status === 201) {
        setShowStartChat(true);
      } else if (response.status === 202) {
        notifyError(response.data.message[lang]);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  return (
    <div className={styles.readMoreContainer}>
      <StartChat
        show={showStartChat}
        onHide={() => setShowStartChat(false)}
        onTextHandle={onTextHandle}
        onVoiceHandle={onVoiceHandle}
      />
      <ChatPopup
        show={showChatPopup}
        onClose={() => setShowChatPopup(false)}
        setShowDownloadSummary={() => {
          setShowDownloadSummary(true);
          setShowChatPopup(false);
        }}
        header={
          lang === "ar"
            ? sessionDataParsed?.title_arabic
            : sessionDataParsed?.title
        }
        refreshReviews={refreshReviews}
        onDownload={handleDownloadSummary}
        pdfStatus={pdfStatus}
      />

      <VoiceChatPopup
        show={showVoicePopup}
        onClose={() => setShowVoicePopup(false)}
        setShowDownloadSummary={() => {
          setShowDownloadSummary(true);
          setShowVoicePopup(false);
        }}
        header={
          lang === "ar"
            ? sessionDataParsed?.title_arabic
            : sessionDataParsed?.title
        }
        refreshReviews={refreshReviews}
        onDownload={handleDownloadSummary}
        pdfStatus={pdfStatus}
      />

      <AddReviewModal
        show={showAddReviewModal}
        onHide={() => setShowAddReviewModal(false)}
        onSubmit={handleAddReview}
        btnText={btnText}
      />
      <DownloadSummary
        show={showDownloadSummary}
        onHide={() => {
          setShowAddReviewModal(true);
          setShowDownloadSummary(false);
        }}
        onDownload={handleDownloadSummary}
        pdfStatus={pdfStatus}
      />
      <div className={styles.readMore}>
        <SessionDetails
          handleResumeSession={handleResumeSession}
        />
        <div
          ref={reviewsContainerRef}
          className={`${styles.reviewsContainer} ${!reviewsExpanded ? styles.reviewsContainerCollapsed : ""}`}
        >
          <button
            type="button"
            className={styles.reviewsTitleButton}
            onClick={() => setReviewsExpanded((prev) => !prev)}
            aria-expanded={reviewsExpanded}
            data-rtl={lang === "ar" || undefined}
          >
            <h4 className={styles.reviewsTitle}>{reviewsTitle}</h4>
            <span
              className={`${styles.reviewsChevron} ${reviewsExpanded ? styles.reviewsChevronOpen : ""}`}
              aria-hidden
            >
              ▼
            </span>
          </button>
          {reviewsExpanded && (
            <div className={styles.reviews}>
              {!loading &&
                reviewData.allReviews.length > 0 &&
                reviewData.allReviews.map((review, index) => {
                  return (
                    <ReviewCard
                      key={`${review.user_id}-${index}`}
                      {...review}
                    />
                  );
                })}
              {!loading && reviewData.allReviews.length === 0 && (
                <div className={styles.noReviews}>{noReviews}</div>
              )}
              {loading && <DataLoader />}
              {(allReviewsPagination.hasNextPage || loadingMoreAllReviews) && (
                <div
                  ref={allReviewsLoadMoreRef}
                  className={styles.loadMoreSentinel}
                >
                  {loadingMoreAllReviews && <DataLoader />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        ref={reviewHistoryContainerRef}
        className={styles.reviewHistoryContainer}
      >
        <ReviewHistory
          reviewData={reviewData.userReviews}
          reviewCount={
            userReviewsPagination.totalItems || reviewData.userReviews.length
          }
          onReviewChange={refreshReviews}
          handleResumeSession={handleResumeSession}
          onDownload={handleDownloadSummary}
          pdfStatus={pdfStatus}
          loading={loading}
          loadingMore={loadingMoreUserReviews}
          loadMoreSentinelRef={userReviewsLoadMoreRef}
          hasMore={userReviewsPagination.hasNextPage}
        />
      </div>
    </div>
  );
}

export default ReadMore;
